import { DASHBOARD_URL } from '../config'
import { gate } from '../map/enforcement'
import { buildDiagnostic } from './debugLog'

/**
 * Enforcement-aware error states (PRD P0.7).
 * - Seat limit (429): blocking banner; tiles pause via the gate; Retry unblocks.
 * - Subscription / plan-flag (403): full-screen states.
 * - 503: toast with automatic backoff retry honoring Retry-After.
 * No daily-quota state exists: viewer traffic is quota-exempt (revised D2).
 */

// ---- Seat-limit banner -----------------------------------------------------

let bannerEl: HTMLElement | null = null

/** Client-facing button: copies the non-sensitive diagnostic bundle. */
function attachCopyButton(host: HTMLElement, code: string): void {
  const btn = document.createElement('button')
  btn.className = 'btn btn--small copy-diagnostic'
  btn.textContent = 'Copy problem details'
  btn.addEventListener('click', () => {
    navigator.clipboard
      .writeText(buildDiagnostic(code))
      .then(() => {
        btn.textContent = 'Copied ✓'
        setTimeout(() => (btn.textContent = 'Copy problem details'), 2000)
      })
      .catch(() => {/* clipboard unavailable — ignore */})
  })
  host.appendChild(btn)
}

export function showSeatBanner(message: string, onRetry: () => void, code: string): void {
  if (bannerEl) return // already showing
  gate.block(message)
  bannerEl = document.createElement('div')
  bannerEl.className = 'seat-banner'
  bannerEl.setAttribute('role', 'alert')
  bannerEl.innerHTML = `
    <div class="seat-banner__text">
      <strong>Seat limit reached.</strong>
      <span></span>
      <a href="${DASHBOARD_URL}" target="_blank" rel="noopener">Manage active sessions</a>
      <span class="error-code">code ${code}</span>
    </div>
    <button class="btn btn--small">Retry</button>`
  const span = bannerEl.querySelector('.seat-banner__text span:not(.error-code)') as HTMLElement
  const isGeneric = message.startsWith('Request failed')
  span.textContent = isGeneric ? '' : message
  bannerEl.querySelector('button')!.addEventListener('click', () => {
    hideSeatBanner()
    gate.unblock()
    onRetry()
  })
  attachCopyButton(bannerEl, code)
  document.body.appendChild(bannerEl)
}

export function hideSeatBanner(): void {
  bannerEl?.remove()
  bannerEl = null
}

// ---- Full-screen states ----------------------------------------------------

export function renderSubscriptionInactive(
  container: HTMLElement,
  onLogout: () => void,
  code: string,
): void {
  renderFullScreen(
    container,
    'Subscription inactive',
    'Your company’s subscription is not active. Please contact your administrator to restore access.',
    onLogout,
    code,
  )
}

export function renderNotInPlan(
  container: HTMLElement,
  onLogout: () => void,
  code: string,
): void {
  renderFullScreen(
    container,
    'Map viewer not included in your plan',
    'The interactive map viewer is not part of your current plan. Please contact your administrator or DigiGeoData to enable it.',
    onLogout,
    code,
  )
}

function renderFullScreen(
  container: HTMLElement,
  title: string,
  body: string,
  onAction: () => void,
  code: string,
  actionLabel = 'Sign out',
): void {
  container.innerHTML = `
    <div class="screen screen--state">
      <div class="card state-card">
        <h1>${title}</h1>
        <p>${body}</p>
        <p class="error-code">code ${code}</p>
        <div class="state-card__actions"></div>
      </div>
    </div>`
  const actions = container.querySelector('.state-card__actions') as HTMLElement
  const actionBtn = document.createElement('button')
  actionBtn.className = 'btn'
  actionBtn.textContent = actionLabel
  actionBtn.addEventListener('click', onAction)
  actions.appendChild(actionBtn)
  attachCopyButton(actions, code)
}

export function renderCrashScreen(container: HTMLElement): void {
  renderFullScreen(
    container,
    'Something went wrong',
    'The map viewer hit an unexpected error. Copy the details below to report the problem, then reload the page.',
    () => location.reload(),
    'JS-CRASH',
    'Reload',
  )
}

// ---- 503 toast with backoff ------------------------------------------------

let toastEl: HTMLElement | null = null
let toastTimer: ReturnType<typeof setTimeout> | null = null
let backoffSec = 5

/** Reset backoff once tiles succeed again. */
export function resetServiceBackoff(): void {
  backoffSec = 5
}

export function showServiceToast(
  retryAfterSec: number | null,
  onRetry: () => void,
  code: string,
): void {
  if (toastEl) return // a retry is already scheduled
  const delay = retryAfterSec ?? backoffSec
  backoffSec = Math.min(backoffSec * 2, 60)

  toastEl = document.createElement('div')
  toastEl.className = 'service-toast'
  toastEl.setAttribute('role', 'status')
  document.body.appendChild(toastEl)

  let remaining = Math.max(1, Math.round(delay))
  const tick = () => {
    if (!toastEl) return
    // Transient + auto-retry (design spec §C): show the code inline, no copy
    // button — the toast is gone before a user could act on one anyway.
    toastEl.textContent = `Service temporarily unavailable (code ${code}) — retrying in ${remaining}s…`
    if (remaining <= 0) {
      hideServiceToast()
      onRetry()
      return
    }
    remaining -= 1
    toastTimer = setTimeout(tick, 1000)
  }
  tick()
}

export function hideServiceToast(): void {
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = null
  toastEl?.remove()
  toastEl = null
}
